'use client'

/**
 * IdentityChips - the rank line under the username in the navbar identity block.
 *
 * 🚨 WHY IT EXISTS (owner 2026-08-16: "the user profile at top right is not using banner based on
 * money spent ... level 0 looks weird ... this is supposed to show level as well"). Three separate
 * things were wrong in that one small area, and all three were measured against prod before changing
 * anything:
 *
 *   1. THE CHIP SHOWED A ROLE, NOT A RANK. It rendered `user.tier`, which is the role column:
 *      997 accounts hold 'Lurker', 2 hold 'admin', 1 holds 'free'. So the owner's chip read "admin".
 *      The rank earned by money spent is `donorTier`, and that column is HEALTHY - all 18 accounts
 *      with total_donated > 0 carry the correct tier (tier1 at $5-60, tier2 at $100-480). It was
 *      simply never surfaced here.
 *
 *   2. LEVEL 0 IS THE NORM, NOT AN EDGE CASE. Measured: 847 of 1,000 accounts are level 0, 152 are
 *      level 1, one is 116, and `exp` is 0 for every account checked including the owner's. So the
 *      level system exists and nothing feeds it. A bare "0" disc on the avatar reads as broken
 *      because, for almost everyone, it is showing the absence of a system rather than a score.
 *      ⭐ SO LEVEL 0 IS RENDERED AS A STATE, NOT A NUMBER: "New" instead of "0". Any level above 0
 *      shows the number, which is what the owner asked for and what the 152 level-1 accounts earn.
 *
 *   3. THE TIER NAME WAS NOWHERE IN THE NAVBAR. Neophyte, Patron, Steward ... Sovereign are defined
 *      in donor-utils.ts and rendered on the profile page, but the navbar showed only a coloured
 *      ring. Naming it here means the ring and the chip corroborate each other.
 *
 * 🚨🚨 2026-08-20, THE LEVEL SYSTEM IS PROVABLY INERT AND THE "New" CHIP IS GONE. The 08-16 note
 * below predicted this ("either wire levels to an action worth rewarding, or drop the chip") and the
 * owner then reported the symptom it predicted: "so many weird badges ... my level does not show".
 * Measured on prod, whole users table, not a sample:
 *     max(exp) = 0        across ALL 1,037 accounts
 *     exp > 0             0 accounts
 *     level 0             874    level 1  162    level 116  1
 * And a codebase-wide search for any write to exp/level finds NOTHING - no award site exists. So the
 * chip was not reporting a low score, it was reporting the absence of a system, on 874 accounts.
 *   ⛔ THE TOOLTIP WAS AN ACTIVE FALSEHOOD: "earn XP by using the site" promised something no code
 * path can deliver. That is worse than an ugly chip.
 *   ⭐ SO THE CHIP NOW DEGRADES HONESTLY INSTEAD OF BEING DELETED: it renders when there IS a level
 * (163 accounts have one) and renders nothing when there is not. If XP is ever wired up, the chip
 * reappears on its own with no code change - which is why this is not a deletion.
 *
 * ⚠ THE OWNER'S OWN ACCOUNT HAS total_donated = 0.00 AND donor_tier = NULL. Their Sovereign frame
 * comes entirely from the `isCreator` username override in Navbar.tsx, not from the data. That is a
 * deliberate existing rule, kept as-is, but it means this component must never be "verified" by
 * looking at the owner's avatar alone - it proves the override works, not the tier ladder.
 */

import { DONOR_TIERS, type DonorTier } from '@/lib/donor-utils'

/**
 * Chip colours per tier, matching the avatar ring so the two never disagree.
 *
 * ⭐ 2026-08-22 COLOUR PASS (owner: "the coloring and everything else needs improvement"). The old
 * chips filled with a muddy `*-950/40` on a full pill, so every rank read as the same dark smudge and
 * Sovereign did not look like the top of a ten-step ladder. Now: a crisp `*-500/12` tint with a
 * brighter `*-300` label on a tag-shaped micro-badge, and Sovereign alone gets a red→gold GRADIENT
 * with a faint ember glow so "the best" reads as the best at a glance.
 */
const TIER_CHIP: Record<string, string> = {
  tier1: 'border-amber-600/50 bg-amber-500/12 text-amber-300',
  tier2: 'border-zinc-400/45 bg-zinc-300/12 text-zinc-200',
  tier3: 'border-sky-500/45 bg-sky-500/12 text-sky-300',
  tier4: 'border-emerald-500/45 bg-emerald-500/12 text-emerald-300',
  tier5: 'border-cyan-500/45 bg-cyan-500/12 text-cyan-300',
  tier6: 'border-violet-500/45 bg-violet-500/12 text-violet-300',
  tier7: 'border-orange-500/45 bg-orange-500/12 text-orange-300',
  tier8: 'border-yellow-500/50 bg-yellow-500/12 text-yellow-300',
  tier9: 'border-pink-500/45 bg-pink-500/12 text-pink-300',
  // Sovereign is DEEP RED — never gold/orange (owner: "it looks too close to magnate ... supposed to
  // be that red color"). A red chip with a red ember glow, matching the red Sovereign ring.
  tier10:
    'border-red-500/70 bg-red-500/20 text-red-200 shadow-[0_0_12px_-3px_rgba(239,68,68,0.7)]',
}

const CHIP =
  'inline-flex items-center rounded-md border px-1.5 py-[3px] text-[10px] font-bold uppercase tracking-[0.07em] leading-none whitespace-nowrap'

export default function IdentityChips({
  donorTier,
  level,
  isAdmin = false,
  variant = 'full',
  showLevel = true,
}: {
  donorTier?: string | null
  level?: number | null
  isAdmin?: boolean
  /** Hide the level chip where a richer level block (CrestLevel) already shows it, e.g. the dropdown. */
  showLevel?: boolean
  /**
   * ⭐ 'compact' IS THE COLLAPSED NAVBAR BUTTON: RANK ONLY. The owner's screenshot showed four chips
   * (Sovereign / New / admin / ∞ Unlimited) crowded into ~200px beside the avatar - "so many weird
   * badges". Rank is the one fact that belongs in a collapsed control; role and plan are detail, and
   * detail belongs in the dropdown, which already renders this same component in 'full'.
   * ⛔ Nothing is hidden, only moved. Both surfaces still render ONE component so they cannot drift
   * apart again, which was defect 3 of the original three.
   */
  variant?: 'compact' | 'full'
}) {
  const tierKey = donorTier && donorTier !== 'none' ? donorTier : null
  const tierName = tierKey ? DONOR_TIERS[tierKey as DonorTier]?.name : null
  const lvl = typeof level === 'number' && Number.isFinite(level) ? level : 0

  return (
    <>
      {tierName ? (
        <span
          className={`${CHIP} ${TIER_CHIP[tierKey as string] ?? 'border-zinc-500/50 bg-zinc-400/12 text-zinc-300'}`}
          title={`${tierName} - ${DONOR_TIERS[tierKey as DonorTier]?.description ?? ''}`}
        >
          {tierName}
        </span>
      ) : (
        // No donation on record. "Lurker" is the existing word for this and is kept rather than
        // inventing a second vocabulary for the same state.
        <span className={`${CHIP} border-zinc-500/50 bg-zinc-400/10 text-zinc-400`} title="No donor tier yet">
          Lurker
        </span>
      )}

      {/* ⭐ ONLY WHEN THERE IS A LEVEL. max(exp) is 0 across all 1,037 accounts and no code path
          awards XP, so "New" was rendering the absence of a system on 874 accounts - and promising
          XP that cannot be earned. A level chip appears the moment a level exists. */}
      {variant === 'full' && showLevel && lvl > 0 && (
        <span
          className={`${CHIP} border-amber-500/45 bg-amber-500/12 text-amber-300`}
          title={`Level ${lvl}`}
        >
          Lv {lvl}
        </span>
      )}

      {variant === 'full' && isAdmin && (
        <span className={`${CHIP} border-sky-500/45 bg-sky-500/12 text-sky-300`} title="Administrator">
          admin
        </span>
      )}
    </>
  )
}
